import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, ArrowRight } from "lucide-react";
import { toast } from "react-toastify";
import authenticationServices from "../Services/authenticationServices";
import { resolveWarehouseFromList } from "../Services/warehouseContext";
import { encryptData } from "../Services/encryptDecrypt";
import { Spinner } from "./ui/caliper";
import curebayLogo from "../Assets/CurebayLogo.svg";

/* Resolve the user's warehouse from warehouse/list (stateCode + circleCode)
   and persist it. The Supply module identifies a warehouse by its
   pharmacyCode, and the invoice header reads the full pharmacy detail.
   Best-effort: a failure here must not block login. */
async function resolveAndStoreWarehouse(user) {
  try {
    const res = await authenticationServices.getwareHouseDetails();
    const list = res?.data?.successList || [];
    const wh = resolveWarehouseFromList(list, {
      state: user.state,
      circleCode: user.circleCode,
    });
    if (!wh) {
      console.warn("No warehouse matched for user", user.state, user.circleCode);
      return;
    }
    localStorage.setItem("warehousePharmacyCode", wh.pharmacyCode);
    localStorage.setItem("warehouseId", String(wh.id));
    localStorage.setItem(
      "warehouseInfo",
      JSON.stringify({
        pharmacyCode: wh.pharmacyCode,
        pharmacyName: wh.pharmacyName,
        address: wh.address,
        cityName: wh.cityName,
        pincode: wh.pincode,
        stateCode: wh.stateCode,
        stateName: wh.stateName,
        dl: wh.dl,
        gstin: wh.gstin,
        pan: wh.pan,
      })
    );
  } catch (err) {
    console.error("Warehouse resolution failed:", err);
  }
}

function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ userCode: "", passWord: "" });

  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();

  /* Already signed in? Skip the form. The session (access_token + loginObj)
     lives in localStorage, which every tab of the same origin shares — so a
     tab opened after logging in elsewhere lands straight on the dashboard
     instead of asking for the password again. The `storage` listener covers
     the case where this login tab is already open when a sign-in happens in
     another tab. */
  useEffect(() => {
    const isAuthed = () =>
      !!localStorage.getItem("access_token") && !!localStorage.getItem("loginObj");
    if (isAuthed()) {
      navigate("/dashBoard", { replace: true });
      return undefined;
    }
    const onStorage = (e) => {
      if ((e.key === "access_token" || e.key === "loginObj" || e.key === null) && isAuthed()) {
        navigate("/dashBoard", { replace: true });
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [navigate]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.userCode || !formData.passWord) return;

    setLoading(true);
    try {
      const payload = {
        userCode: formData.userCode,
        passWord: window.btoa(formData.passWord),
        channel: "W",
      };
      const encryptedData = encryptData(payload);
      const url = `${baseUrl}gcp/auth-token`;

      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(encryptedData),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const tokenResponse = await response.json();
      if (tokenResponse.access_token) {
        localStorage.clear();
        localStorage.setItem("access_token", tokenResponse.access_token);
        localStorage.setItem("access_name", formData.userCode.toUpperCase());
        localStorage.setItem("refresh_token", tokenResponse?.refresh_token);

        const loginRes = await authenticationServices.loginApi(payload);
        const userData = loginRes.data;
        if (userData) {
          const hasPharmacyRole = userData.user.userRolesList.some(
            (role) => role.roleCode === "PH" || role.roleCode === "PHHEAD"
          );
          if (hasPharmacyRole) {
            localStorage.setItem("loginObj", JSON.stringify(userData.user));
            localStorage.setItem("userCode", userData.user.code);
            localStorage.setItem("userEmail", userData.user.email);
            localStorage.setItem("userState", userData.user.state);
            localStorage.setItem("circleCode", userData?.user?.circleCode || "N");
            await resolveAndStoreWarehouse(userData.user);
            navigate("/dashBoard");
          } else {
            toast.error("This account does not have pharmacy access.");
          }
        } else {
          toast.error("Login failed — please try again.");
        }
      }
    } catch (error) {
      console.error("Login error:", error);
      const msg = String(error?.message || "");
      if (error?.name === "TypeError" || /failed to fetch|networkerror/i.test(msg)) {
        toast.error("Can't reach the server — check your connection or try again later.");
      } else if (/HTTP 5\d\d/.test(msg)) {
        toast.error("The server is temporarily unavailable. Please try again shortly.");
      } else if (/HTTP 401|HTTP 403/.test(msg)) {
        toast.error("Incorrect email or password.");
      } else {
        toast.error("Login failed — please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="flex w-full min-h-screen lg:h-screen bg-canvas">
      {/* ===== hero — the one place a gradient is permitted ===== */}
      <div
        className="hidden lg:flex w-[46%] flex-col justify-between p-12 relative overflow-hidden"
        style={{
          background:
            "linear-gradient(178deg, var(--ink-600) 0%, var(--ink-700) 60%, var(--ink-800) 100%)",
        }}
      >
        {/* hairline grid texture */}
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "linear-gradient(var(--neutral-0) 1px, transparent 1px), linear-gradient(90deg, var(--neutral-0) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
          }}
        />
        <img
          src={curebayLogo}
          alt="CureBay"
          className="h-9 relative brightness-0 invert"
        />

        <div className="relative">
          <div className="cb-eyebrow text-ink-200 mb-4">Operator console</div>
          <h1 className="cb-display text-[44px] text-white leading-[1.1]">
            Orders, fulfilment &amp; reporting —
            <br />
            in one precise console.
          </h1>
          <p className="text-ink-100 text-t-base mt-5 max-w-md leading-relaxed">
            Track every CHE, B2C, B2B and eyewear order through enquiry,
            confirmation, packing and dispatch. Built for the people who run
            the warehouse.
          </p>

          <div className="flex gap-8 mt-9">
            {[
              { k: "4", v: "Order channels" },
              { k: "100%", v: "Batch-traceable" },
              { k: "Live", v: "Sync status" },
            ].map((s) => (
              <div key={s.v}>
                <div className="font-mono text-t-2xl font-semibold text-white tabular-nums">
                  {s.k}
                </div>
                <div className="text-ink-200 text-t-xs uppercase tracking-[0.06em] mt-1">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative text-ink-300 text-t-xs font-mono">
          © {new Date().getFullYear()} CureBay · for a healthier India
        </div>
      </div>

      {/* ===== form ===== */}
      <div className="flex-1 flex flex-col justify-center items-center px-6 py-10">
        <div className="w-full max-w-[400px]">
          <img src={curebayLogo} alt="CureBay" className="h-10 mb-10 lg:hidden" />

          <div className="cb-eyebrow mb-2">Sign in</div>
          <h2 className="text-t-3xl font-semibold text-fg-strong tracking-[-0.01em]">
            Log in to your account
          </h2>
          <p className="text-fg-muted text-t-base mt-2 mb-8">
            Enter your email address and password to continue.
          </p>

          <form onSubmit={handleFormSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="userCode" className="text-t-sm font-medium text-fg-default">
                Email address
              </label>
              <div className="flex items-center h-10 px-3 gap-2.5 border border-hairline rounded-cb-sm bg-panel focus-within:border-ink-500 focus-within:shadow-cb-focus transition-shadow">
                <Mail size={16} strokeWidth={1.75} className="text-fg-muted shrink-0" />
                <input
                  id="userCode"
                  type="email"
                  name="userCode"
                  placeholder="you@curebay.in"
                  required
                  value={formData.userCode}
                  onChange={handleInputChange}
                  className="flex-1 min-w-0 bg-transparent outline-none border-0 text-t-base text-fg-default placeholder:text-fg-subtle"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="passWord" className="text-t-sm font-medium text-fg-default">
                Password
              </label>
              <div className="flex items-center h-10 px-3 gap-2.5 border border-hairline rounded-cb-sm bg-panel focus-within:border-ink-500 focus-within:shadow-cb-focus transition-shadow">
                <Lock size={16} strokeWidth={1.75} className="text-fg-muted shrink-0" />
                <input
                  id="passWord"
                  type={showPassword ? "text" : "password"}
                  name="passWord"
                  placeholder="••••••••"
                  required
                  value={formData.passWord}
                  onChange={handleInputChange}
                  className="flex-1 min-w-0 bg-transparent outline-none border-0 text-t-base text-fg-default placeholder:text-fg-subtle"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="text-fg-muted hover:text-fg-strong transition-colors shrink-0"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeOff size={16} strokeWidth={1.75} />
                  ) : (
                    <Eye size={16} strokeWidth={1.75} />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="mt-2 h-10 inline-flex items-center justify-center gap-2 rounded-cb-sm bg-ink-600 text-fg-on-ink text-t-base font-semibold hover:bg-ink-700 active:bg-ink-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Spinner size={16} className="text-white" />
                  Signing in…
                </>
              ) : (
                <>
                  Log in
                  <ArrowRight size={16} strokeWidth={2} />
                </>
              )}
            </button>
          </form>

          <p className="cb-caption mt-8">
            Trouble signing in? Contact your pharmacy administrator.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
